import { getStoredAuth, setStoredAuth, completeLogout } from '../utils/auth';
import { getFromCache, setInCache, clearCache as clearMemoryCache } from '../utils/memoryCache';
import { dedupRequest } from '../utils/requestDeduplication';
// Add Firebase imports with Auth
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithCustomToken } from "firebase/auth"; // Added for auth

// Queue for pending requests during token refresh
let isRefreshing = false;
const requestQueue: Array<() => void> = [];
let baseApiUrl: string = '';

// Add a flag to track if API is configured
let isApiConfigured: boolean = false;

// Firebase configuration - matching the one from investmentAPI.ts
const firebaseConfig = {
  apiKey: "AIzaSyAHXekrQlHF4PKjnV4X6EtLpuSHQ7jiRGU",
  authDomain: "nimble-net-279220.firebaseapp.com",
  projectId: "nimble-net-279220",
  storageBucket: "nimble-net-279220.firebasestorage.app",
  messagingSenderId: "185005193682",
  appId: "1:185005193682:web:356662f98354c8c762700b"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Get a reference to Firebase Storage
const storage = getStorage(app);
// Get a reference to Firebase Auth
const firebaseAuth = getAuth(app);

// Track if Firebase Auth is initialized with the current user's token
let isFirebaseAuthInitialized = false;
// Track if initialization is in progress to prevent parallel requests
let isFirebaseAuthInitializing = false;
// Promise to track ongoing initialization
let firebaseAuthInitPromise: Promise<boolean> | null = null;

// Process all pending requests
const processQueue = () => {
  requestQueue.forEach((callback) => callback());
  requestQueue.length = 0;
};

/**
 * Configure the API with base URL
 */
export const configureApi = (baseUrl: string): void => {
  baseApiUrl = baseUrl;
  isApiConfigured = true;
  console.log('API configured with base URL:', baseUrl);
};

/**
 * Get the configured base URL
 */
export const getBaseUrl = (): string => {
  if (!isApiConfigured) {
    console.warn('API not configured yet, returning empty base URL');
  }
  return baseApiUrl;
};

/**
 * Check if the API is configured
 */
export const isApiReady = (): boolean => {
  return isApiConfigured && !!baseApiUrl;
};

/**
 * API request options extending the standard fetch RequestInit
 */
export interface ApiRequestOptions extends RequestInit {
  requireAuth?: boolean;
  skipCache?: boolean;
  cacheKey?: string;
  cacheTime?: number; // Cache time in milliseconds
}

/**
 * Core function to make API requests with automatic token handling
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  // Ensure baseApiUrl is set
  if (!isApiConfigured || !baseApiUrl) {
    console.error('API not configured. baseApiUrl:', baseApiUrl, 'isApiConfigured:', isApiConfigured);
    throw new Error('API not configured. Call configureApi() before making requests.');
  }

  const url = endpoint.startsWith('http') ? endpoint : `${baseApiUrl}${endpoint}`;
  const { requireAuth = true, skipCache = false, cacheKey, cacheTime = 5 * 60 * 1000, ...fetchOptions } = options;
  
  // Check memory cache if enabled
  if (cacheKey && !skipCache) {
    const cachedData = getFromCache<T>(cacheKey);
    if (cachedData) {
      console.log(`Cache hit for ${cacheKey}`);
      return cachedData;
    }
    console.log(`Cache miss for ${cacheKey}`);
  }
  
  // If we have a cache key, use request deduplication
  if (cacheKey) {
    return dedupRequest<T>(
      `req-${cacheKey}`, 
      () => performApiRequest<T>(url, requireAuth, cacheKey, cacheTime, fetchOptions),
      60000 // 60 second timeout
    );
  }
  
  // No cache key, just make the request directly
  return performApiRequest<T>(url, requireAuth, cacheKey, cacheTime, fetchOptions);
};

/**
 * Internal function that actually performs the API request
 * Extracted to be used by the deduplication utility
 */
async function performApiRequest<T>(
  url: string,
  requireAuth: boolean,
  cacheKey: string | undefined,
  cacheTime: number,
  fetchOptions: RequestInit
): Promise<T> {
  const auth = getStoredAuth();
  
  // remove double-slash from URL if present outside of the :// protocol (e.g., http://)
  // This is a workaround for some APIs that don't handle double slashes well
  if (url.includes('//')) {
    console.log(`Removing double slashes from URL: ${url}`);
    url = url.replace(/([^:]\/)\/+/g, '$1');
    console.log(`Updated URL: ${url}`);
  }
  // Prepare headers properly typed as Record<string, string>
  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string> || {}),
  };
  
  // Add auth headers if required and token available
  if (requireAuth && auth?.token) {
    headers['Authorization'] = `Bearer ${auth.token}`;
    headers['Access-Control-Allow-Headers'] = 'Authorization';
  }
  
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      mode: 'cors',
    });
    
    // Handle successful responses
    if (response.ok) {
      // For 204 No Content responses, return null
      if (response.status === 204) {
        return null as T;
      }
      
      // For others, parse JSON
      const data = await response.json();
      
      // Store in memory cache if cacheKey provided
      if (cacheKey) {
        setInCache(cacheKey, data, cacheTime);
      }
      
      return data;
    }
    
    // Handle authentication errors
    if (response.status === 401) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<T>((resolve, reject) => {
          requestQueue.push(() => {
            apiRequest<T>(url, { 
              requireAuth, 
              skipCache: true, // Skip cache when retrying
              cacheKey,
              cacheTime,
              ...fetchOptions 
            })
              .then(resolve)
              .catch(reject);
          });
        });
      }
      
      // Try to refresh the token
      isRefreshing = true;
      
      try {
        if (!auth?.sessionId) {
          throw new Error('No session ID available');
        }
        
        // console.log('DEBUG: apiServices: Refreshing token...');
        const refreshResponse = await fetch(`${new URL(url).origin}?verifyToken&sessionId=${auth.sessionId}`, {
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Access-Control-Allow-Headers': 'Authorization',
          },
          method: 'GET',
          mode: 'cors',
        });
        
        if (!refreshResponse.ok) {
          throw new Error('Token refresh failed');
        }
        
        const refreshData = await refreshResponse.json();
        
        if (!refreshData.valid || !refreshData.payload) {
          throw new Error('Invalid token refresh response');
        }
        
        // Update the token in storage
        auth.token = refreshData.payload.token;
        auth.user = refreshData.payload.user;
        setStoredAuth(auth);
        
        // If the payload includes a firebase_token, initialize Firebase Auth
        if (refreshData.payload.firebase_token) {
          // Initialize Firebase Auth with the new token, forcing refresh even if already initialized
          initializeFirebaseAuth(true).catch(err => {
            console.error('Error initializing Firebase Auth after token refresh:', err);
          });
        }
        
        // Process any queued requests
        processQueue();
        isRefreshing = false;
        
        // Retry the original request with the new token
        return apiRequest<T>(url, { 
          requireAuth, 
          skipCache: true,  // Skip cache when retrying
          cacheKey, 
          cacheTime,
          ...fetchOptions 
        });
      } catch (refreshError) {
        // If refresh fails, log out the user
        isRefreshing = false;
        completeLogout();
        throw new Error('Your session has expired. Please log in again.');
      }
    }
    
    // Handle permission errors
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      
      if (errorData.error === 'REGISTRATION_REQUIRED') {
        throw new Error('REGISTRATION_REQUIRED');
      } else if (errorData.error === 'SUBSCRIPTION_EXPIRED' || errorData.error === 'INSUFFICIENT_PERMISSIONS') {
        throw new Error('SUBSCRIPTION_REQUIRED');
      }
      
      throw new Error(errorData.message || 'You do not have permission to access this resource');
    }
    
    // Handle rate limiting
    if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    // Handle other errors
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  } catch (error: any) {
    console.error(`API request failed for ${url}:`, error);
    throw error;
  }
}

/**
 * Initialize Firebase authentication with the user's firebase_token
 * Prevents multiple parallel initialization requests
 */
export const initializeFirebaseAuth = async (forceRefresh: boolean = false): Promise<boolean> => {
  // If already initialized and no force refresh, return immediately
  // console.log('Firebase Auth: Initializing...');
  if (isFirebaseAuthInitialized && !forceRefresh) {
    console.log('Firebase Auth: Already initialized, skipping (use forceRefresh=true to reinitialize)');
    return true;
  }
  
  // If initialization is already in progress, return the existing promise
  if (isFirebaseAuthInitializing && firebaseAuthInitPromise && !forceRefresh) {
    console.log('Firebase Auth: Initialization already in progress, returning existing promise');
    return firebaseAuthInitPromise;
  }
  
  const auth = getStoredAuth();
  const firebaseToken = (!auth || !auth.user || (auth.user.role === 'unregistered')) ? null : auth?.user?.firebase_token;
  
  if (!firebaseToken) {
    console.log('Firebase Auth: No token available, signing out and skipping initialization');
    // Sign out from Firebase Auth to ensure clean state
    await firebaseAuth.signOut();
    isFirebaseAuthInitialized = false;
    return false;
  }
  
  // Set flag and create new promise
  isFirebaseAuthInitializing = true;
  firebaseAuthInitPromise = (async () => {
    try {
      console.log('Firebase Auth: Initializing with custom token');
      await signInWithCustomToken(firebaseAuth, firebaseToken);
      console.log('Firebase Auth: Successfully initialized');
      isFirebaseAuthInitialized = true;
      return true;
    } catch (error) {
      console.error('Firebase Auth: Failed to initialize with token:', error);
      // Ensure we sign out on error to prevent stale auth state
      await firebaseAuth.signOut();
      isFirebaseAuthInitialized = false;
      return false;
    } finally {
      isFirebaseAuthInitializing = false;
      firebaseAuthInitPromise = null;
    }
  })();
  
  return firebaseAuthInitPromise;
};

/**
 * Get auth headers for cases where you need them separately
 */
export const getAuthHeaders = (): Record<string, string> => {
  const auth = getStoredAuth();
  return auth?.token ? {
    'Authorization': `Bearer ${auth.token}`,
    'Access-Control-Allow-Headers': 'Authorization',
  } : {};
};

/**
 * Clear all API caches - useful when logging out or refreshing data
 */
export const clearAllCaches = (): void => {
  clearMemoryCache();
  // Also sign out from Firebase
  signOutFirebase().catch(err => {
    console.error('Error signing out from Firebase during cache clear:', err);
  });
};

/**
 * Sign out from Firebase Auth
 */
export const signOutFirebase = async (): Promise<void> => {
  try {
    console.log('Firebase Auth: Signing out');
    await firebaseAuth.signOut();
    isFirebaseAuthInitialized = false;
    console.log('Firebase Auth: Successfully signed out');
  } catch (error) {
    console.error('Firebase Auth: Error signing out:', error);
  }
};

/**
 * Fetches content from Firebase Storage
 * Uses firebase_token from user auth if available
 */
export const getFileContentFromFirebase = async (filePath: string): Promise<any> => {
  console.log(`Firebase Fetch: Getting content for "${filePath}"...`);
  
  try {
    // Check if we need to initialize Firebase Auth
    const auth = getStoredAuth();
    const firebaseToken = (!auth || !auth.user || (auth.user.role === 'unregistered')) ? null : auth?.user?.firebase_token;
    
    // Check current Firebase auth state
    const currentFirebaseUser = firebaseAuth.currentUser;
    console.log('Firebase Fetch: Current Firebase user:', currentFirebaseUser?.uid || 'none');
    
    if (firebaseToken) {
      // Always check token validity by comparing with current user
      if (!isFirebaseAuthInitialized || !currentFirebaseUser) {
        console.log('Firebase Fetch: Firebase Auth not initialized, initializing now...');
        await initializeFirebaseAuth();
      } else {
        // If Firebase user is initialized but token might have changed
        console.log('Firebase Fetch: Firebase Auth initialized, proceeding with request');
      }
    } else if (currentFirebaseUser) {
      // We have a Firebase user but no token in auth - this is a stale state, sign out
      console.log('Firebase Fetch: No token but Firebase user exists, signing out');
      await signOutFirebase();
    }

    // Create a reference to the file
    const fileRef = ref(storage, filePath);
    
    // getDownloadURL uses the current Firebase Auth context
    const url = await getDownloadURL(fileRef);
    console.log(`Firebase Fetch: Download URL obtained for "${filePath}"`);
    
    // Fetch the content using the URL provided by Firebase
    const response = await fetch(url, { 
      method: 'GET', 
      mode: 'cors',
      credentials: 'omit' // Don't send cookies
    });
    
    if (!response.ok) {
      throw new Error(`Firebase Fetch: Failed to fetch file content (${response.status} ${response.statusText})`);
    }
    
    // Check content type to determine how to parse the response
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
      // Try to parse as JSON if it looks like JSON
      if (data && 
          (data.trim().startsWith('{') || data.trim().startsWith('[')) && 
          (data.trim().endsWith('}') || data.trim().endsWith(']'))) {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.log('Firebase Fetch: Content is not valid JSON, returning as text');
        }
      }
    }
    
    console.log(`Firebase Fetch: Successfully fetched content for "${filePath}".`);
    return data;
  } catch (error) {
    console.error(`Firebase Fetch: Error getting content for "${filePath}":`, error);
    throw error; // Simply throw the error without any fallback
  }
};
